import { BoxStaffPermissions, BoxStaffRole } from "@modules/boxStaff/gateway/boxStaffDto"
import { word } from "@modules/core/utils/i18n"

export const roleBoxStaffPermissions: Record<BoxStaffRole, BoxStaffPermissions[]> = {
  [BoxStaffRole.FINANCIAL]: [BoxStaffPermissions.SEE_FINANCIAL],
  [BoxStaffRole.COACH]: [
    BoxStaffPermissions.SEE_TIMETABLES,
    BoxStaffPermissions.MANAGE_SLOTS,
    BoxStaffPermissions.MANAGE_PROGRAMMING,
    BoxStaffPermissions.SEE_OFFERS,
    BoxStaffPermissions.SEE_MEMBERS,
    BoxStaffPermissions.SEE_LEADS,
    BoxStaffPermissions.SEE_STAFF,
    BoxStaffPermissions.MANAGE_LEAD,
  ],
  [BoxStaffRole.HEAD_COACH]: [
    BoxStaffPermissions.SEE_TIMETABLES,
    BoxStaffPermissions.MANAGE_SLOTS,
    BoxStaffPermissions.MANAGE_TYPICAL_WEEK,
    BoxStaffPermissions.MANAGE_PROGRAMMING,
    BoxStaffPermissions.CREATE_ACTIVITY,
    BoxStaffPermissions.SEE_OFFERS,
    BoxStaffPermissions.SEE_MEMBERS,
    BoxStaffPermissions.SEE_LEADS,
    BoxStaffPermissions.EDIT_MEMBER,
    BoxStaffPermissions.ARCHIVE_MEMBER,
    BoxStaffPermissions.SEE_TERMINATED,
    BoxStaffPermissions.SEE_STAFF,
    BoxStaffPermissions.MANAGE_LEAD,
  ],
  [BoxStaffRole.CO_OWNER]: [
    BoxStaffPermissions.SEE_TIMETABLES,
    BoxStaffPermissions.MANAGE_SLOTS,
    BoxStaffPermissions.CREATE_ACTIVITY,
    BoxStaffPermissions.MANAGE_TYPICAL_WEEK,
    BoxStaffPermissions.MANAGE_PROGRAMMING,
    BoxStaffPermissions.SEE_OFFERS,
    BoxStaffPermissions.CREATE_OFFER,
    BoxStaffPermissions.EDIT_OFFER,
    BoxStaffPermissions.SEND_OFFER,
    BoxStaffPermissions.DELETE_OFFER,
    BoxStaffPermissions.SEE_MEMBERS,
    BoxStaffPermissions.SEE_LEADS,
    BoxStaffPermissions.SEE_TERMINATED,
    BoxStaffPermissions.EDIT_MEMBER,
    BoxStaffPermissions.EDIT_MEMBER_OFFER,
    BoxStaffPermissions.ARCHIVE_MEMBER,
    BoxStaffPermissions.SEE_STAFF,
    BoxStaffPermissions.ADD_STAFF,
    BoxStaffPermissions.CHANGE_ROLE_STAFF,
    BoxStaffPermissions.REMOVE_STAFF,
    BoxStaffPermissions.SEE_FINANCIAL,
    BoxStaffPermissions.SEE_GYM_INFORMATION,
    BoxStaffPermissions.EDIT_GYM_INFORMATION,
    BoxStaffPermissions.MANAGE_LEAD,
  ],
  [BoxStaffRole.OWNER]: [
    BoxStaffPermissions.SEE_TIMETABLES,
    BoxStaffPermissions.MANAGE_SLOTS,
    BoxStaffPermissions.CREATE_ACTIVITY,
    BoxStaffPermissions.MANAGE_TYPICAL_WEEK,
    BoxStaffPermissions.MANAGE_PROGRAMMING,
    BoxStaffPermissions.SEE_OFFERS,
    BoxStaffPermissions.CREATE_OFFER,
    BoxStaffPermissions.EDIT_OFFER,
    BoxStaffPermissions.SEND_OFFER,
    BoxStaffPermissions.DELETE_OFFER,
    BoxStaffPermissions.SEE_MEMBERS,
    BoxStaffPermissions.SEE_LEADS,
    BoxStaffPermissions.SEE_TERMINATED,
    BoxStaffPermissions.EDIT_MEMBER,
    BoxStaffPermissions.EDIT_MEMBER_OFFER,
    BoxStaffPermissions.ARCHIVE_MEMBER,
    BoxStaffPermissions.SEE_STAFF,
    BoxStaffPermissions.ADD_STAFF,
    BoxStaffPermissions.CHANGE_ROLE_STAFF,
    BoxStaffPermissions.REMOVE_STAFF,
    BoxStaffPermissions.SEE_FINANCIAL,
    BoxStaffPermissions.SEE_GYM_INFORMATION,
    BoxStaffPermissions.EDIT_GYM_INFORMATION,
    BoxStaffPermissions.DELETE_GYM,
    BoxStaffPermissions.SEE_BANK_DETAILS,
    BoxStaffPermissions.EDIT_BANK_DETAILS,
    BoxStaffPermissions.MANAGE_LEAD,
  ],
}

export const getAllowedRolesForPermission = (permission: BoxStaffPermissions) => {
  return {
    owner: roleBoxStaffPermissions[BoxStaffRole.OWNER].includes(permission),
    coOwner: roleBoxStaffPermissions[BoxStaffRole.CO_OWNER].includes(permission),
    headCoach: roleBoxStaffPermissions[BoxStaffRole.HEAD_COACH].includes(permission),
    coach: roleBoxStaffPermissions[BoxStaffRole.COACH].includes(permission),
    financial: roleBoxStaffPermissions[BoxStaffRole.FINANCIAL].includes(permission),
  }
}

export const boxStaffPermissions = () => [
  {
    category: word("box.staff.permissionsModal.feature.planning.label"),
    features: [
      {
        name: word("box.staff.permissionsModal.feature.planning.seeTimetables"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_TIMETABLES),
      },
      {
        name: word("box.staff.permissionsModal.feature.planning.createActivity"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.CREATE_ACTIVITY),
      },
      {
        name: word("box.staff.permissionsModal.feature.planning.manageTypicalWeek"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.MANAGE_TYPICAL_WEEK),
      },
      {
        name: word("box.staff.permissionsModal.feature.planning.manageSlots"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.MANAGE_SLOTS),
      },
    ],
  },
  {
    category: word("box.staff.permissionsModal.feature.programming.label"),
    features: [
      {
        name: word("box.staff.permissionsModal.feature.programming.manageProgrammings"), // Manage programmings
        ...getAllowedRolesForPermission(BoxStaffPermissions.MANAGE_PROGRAMMING),
      },
    ],
  },
  {
    category: word("box.staff.permissionsModal.feature.offers.label"),
    features: [
      {
        name: word("box.staff.permissionsModal.feature.offers.seeList"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_OFFERS),
      },
      {
        name: word("box.staff.permissionsModal.feature.offers.createOffer"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.CREATE_OFFER),
      },
      {
        name: word("box.staff.permissionsModal.feature.offers.editOffer"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.EDIT_OFFER),
      },
      {
        name: word("box.staff.permissionsModal.feature.offers.deleteOffer"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.DELETE_OFFER),
      },
    ],
  },
  {
    category: word("box.staff.permissionsModal.feature.members.label"),
    features: [
      {
        name: word("box.staff.permissionsModal.feature.members.seeList"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_MEMBERS),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.seeListofLeads"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_LEADS),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.manageLeads"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.MANAGE_LEAD),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.seeListofTerminated"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_TERMINATED),
      },
      // {
      //   name: word("box.staff.permissionsModal.feature.members.searchMember"),
      //   owner: true,
      //   coOwner: true,
      //   headCoach: true,
      //   coach: true,
      //   financial: false,
      // },
      {
        name: word("box.staff.permissionsModal.feature.members.editMemberFile"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.EDIT_MEMBER),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.sendAnOffer"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEND_OFFER),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.archiveMemberFile"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.ARCHIVE_MEMBER),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.seeStaffMember"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_STAFF),
      },
      // {
      //   name: word("box.staff.permissionsModal.feature.members.seeStaffMemberHistory"), // Add rule by default for see my own history
      //   owner: true,
      //   coOwner: true,
      //   headCoach: false,
      //   coach: false,
      //   financial: false,
      // },
      {
        name: word("box.staff.permissionsModal.feature.members.addStaffMember"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.ADD_STAFF),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.changeRoleStaff"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.CHANGE_ROLE_STAFF),
      },
      {
        name: word("box.staff.permissionsModal.feature.members.removeStaff"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.REMOVE_STAFF),
      },
    ],
  },
  {
    category: word("box.staff.permissionsModal.feature.finance.label"),
    features: [
      {
        name: word("box.staff.permissionsModal.feature.finance.seeFinancial"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_FINANCIAL),
      },
      // {
      //   name: word("box.staff.permissionsModal.feature.finance.updateData"),
      //   owner: true,
      //   coOwner: true,
      //   headCoach: false,
      //   coach: false,
      //   financial: true,
      // },
    ],
  },
  {
    category: word("box.staff.permissionsModal.feature.admin.label"),
    features: [
      {
        name: word("box.staff.permissionsModal.feature.admin.seeGymInfo"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_GYM_INFORMATION),
      },
      {
        name: word("box.staff.permissionsModal.feature.admin.editGymInfo"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.EDIT_GYM_INFORMATION),
      },
      {
        name: word("box.staff.permissionsModal.feature.admin.deleteGym"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.DELETE_GYM),
      },
      {
        name: word("box.staff.permissionsModal.feature.admin.seeBankDetails"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.SEE_BANK_DETAILS),
      },
      {
        name: word("box.staff.permissionsModal.feature.admin.editBankDetails"),
        ...getAllowedRolesForPermission(BoxStaffPermissions.EDIT_BANK_DETAILS),
      },
    ],
  },
]
